import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { translate, TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ConfirmationService, ConfirmEventType, MenuItem, MessageService } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, filter, firstValueFrom, from, map, Observable, of, switchMap, take, tap } from 'rxjs';

import {
  AllDataEventDisplay,
  AllDataUser,
  EventConfig,
  EventData,
  EventDataSchema,
  EventType,
  getSplitTransaction,
  OrgData,
} from '@pwp-common';

import { EventDialogMode } from '../../components/event/modify-schedule/event-create-dialog/enums';
import { EventCreateDialogComponent } from '../../components/event/modify-schedule/event-create-dialog/event-create-dialog.component';
import { EventSplitDialogComponent } from '../../components/event/modify-schedule/event-split-dialog/event-split-dialog.component';
import { ConfirmWithInputComponent } from '../../components/generic/confirm-with-input/confirm-with-input.component';
import { EventConfigService } from '../../services/event/event-config/event-config.service';
import { EventsService } from '../../services/event/events/events.service';
import { OrgDataService } from '../../services/orgs/org-data/org-data.service';
import { AllDataUserService } from '../../services/user/all-data-user/all-data-user.service';

@UntilDestroy()
@Component({
  selector: 'app-modify-schedule',
  templateUrl: './modify-schedule.component.html',
  styleUrls: ['./modify-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, DialogService],
})
export class ModifyScheduleComponent implements OnInit {
  ////////////////////////////////////////////////////////////////////////
  // Constants
  ////////////////////////////////////////////////////////////////////////

  public selectedEvents: EventData[] = [];

  public allDataUserMap!: Observable<Map<string, AllDataUser>>;

  public eventTypes!: Observable<EventType[]>;

  public orgData!: Observable<OrgData>;

  public contextMenuItems: MenuItem[] = [];

  private selectedEvent: AllDataEventDisplay | null = null;

  @ViewChild(ContextMenu) public readonly contextMenu!: ContextMenu;

  ////////////////////////////////////////////////////////////////////////
  // Lifecycle
  ////////////////////////////////////////////////////////////////////////

  constructor(
    private allDataUserService: AllDataUserService,
    private orgDataService: OrgDataService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private dynamicDialogService: DialogService,
    private confirmationService: ConfirmationService,
    private eventConfigService: EventConfigService,
    private eventsService: EventsService,
  ) {}

  public ngOnInit() {
    console.log('E2E debug: ModifyScheduleComponent.ngOnInit');
    this.allDataUserMap = this.allDataUserService.getDocs();
    this.orgData = this.orgDataService.getOrgData();
    this.eventTypes = this.orgData.pipe(map((z) => z.getEventTypes()));

    this.updateMenuItems();

    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateMenuItems();
    });
  }

  private updateMenuItems(): void {
    this.contextMenuItems = [
      {
        label: this.translocoService.translate('modify-schedule.editShift'),
        icon: 'pi pi-pencil',
        command: () => this.editShift(),
      },
      {
        label: this.translocoService.translate('modify-schedule.splitShift'),
        icon: 'pi pi-sort-alt',
        command: () => this.splitShift(),
      },
      {
        separator: true,
      },
      {
        label: this.translocoService.translate('modify-schedule.deleteShift'),
        icon: 'pi pi-trash',
        command: () => this.deleteShift(),
      },
    ];
  }

  public onEventRightClick(params: { event: AllDataEventDisplay; jsEvent: MouseEvent }): void {
    this.selectedEvent = params.event;
    this.contextMenu.show(params.jsEvent);
  }

  private async editShift(): Promise<void> {
    if (!this.selectedEvent) {
      return;
    }

    const eventData = this.selectedEvent.allDataEvent.getEventData();
    if (!eventData) {
      return;
    }

    const isRepeatingShift = eventData.getEventConfigId() !== EventDataSchema.Defaults.eventConfigId;

    if (isRepeatingShift) {
      this.showEditRepeatingShiftDialog();
      return;
    }

    await this.openEditDialog();
  }

  private showEditRepeatingShiftDialog(): void {
    this.confirmationService.confirm({
      header: this.translocoService.translate('modify-schedule.editRepeatingShiftDialogTitle'),
      message: this.translocoService.translate('modify-schedule.editRepeatingShiftDialogBody'),
      acceptLabel: this.translocoService.translate('modify-schedule.editThisShiftOnly'),
      rejectLabel: this.translocoService.translate('modify-schedule.editThisAndFollowingShifts'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.openEditDialog().catch((error) => {
          console.error('openEditDialog failed:', error);
        });
      },
      reject: (type?: ConfirmEventType) => {
        if (type === ConfirmEventType.REJECT) {
          this.openEditRecurringDialog().catch((error) => {
            console.error('openEditRecurringDialog failed:', error);
          });
        } else {
          this.selectedEvent = null;
        }
      },
    });
  }

  private async openEditDialog(): Promise<void> {
    if (!this.selectedEvent) {
      return;
    }

    const eventData = this.selectedEvent.allDataEvent.getEventData();
    if (!eventData) {
      return;
    }

    const [orgData, allDataUserMap, selectedEventData] = await Promise.all([
      firstValueFrom(this.orgData),
      firstValueFrom(this.allDataUserMap),
      firstValueFrom(this.eventsService.getDoc(eventData.getId())),
    ]);

    if (!selectedEventData) {
      return;
    }

    const userDataArray = Array.from(allDataUserMap.values()).map((u) => u.userData);

    const dialogRef = this.dynamicDialogService.open(EventCreateDialogComponent, {
      header: this.translocoService.translate('modify-schedule.editTitle'),
      width: 'clamp(24rem, 70%, 60rem)',
      data: {
        mode: EventDialogMode.EditNonRecurring,
        eventData: selectedEventData,
        type: selectedEventData.getType(),
        orgData,
        userDataArray,
      },
    });

    dialogRef.onClose.pipe(filter(Boolean), untilDestroyed(this)).subscribe(() => {
      const message = this.translocoService.translate('modify-schedule.editSuccess');
      this.messageService.add({
        severity: 'success',
        summary: this.translocoService.translate('modify-schedule.success'),
        detail: message,
      });
    });
  }

  private async openEditRecurringDialog(): Promise<void> {
    if (!this.selectedEvent) {
      return;
    }

    const eventData = this.selectedEvent.allDataEvent.getEventData();
    if (!eventData) {
      return;
    }

    const eventConfigId = eventData.getEventConfigId();
    if (!eventConfigId || eventConfigId === EventDataSchema.Defaults.eventConfigId) {
      return;
    }

    try {
      const [orgData, allDataUserMap, selectedEventData, eventConfig] = await Promise.all([
        firstValueFrom(this.orgData),
        firstValueFrom(this.allDataUserMap),
        firstValueFrom(this.eventsService.getDoc(eventData.getId())),
        firstValueFrom(this.eventConfigService.getDoc(eventConfigId)),
      ]);

      if (!selectedEventData || !eventConfig) {
        return;
      }

      const userDataArray = Array.from(allDataUserMap.values()).map((u) => u.userData);

      const dialogRef = this.dynamicDialogService.open(EventCreateDialogComponent, {
        header: this.translocoService.translate('modify-schedule.editTitle'),
        width: 'clamp(24rem, 70%, 60rem)',
        data: {
          mode: EventDialogMode.EditRecurring,
          eventData: selectedEventData,
          eventConfig,
          type: selectedEventData.getType(),
          orgData,
          userDataArray,
        },
      });

      dialogRef.onClose.pipe(filter(Boolean), untilDestroyed(this)).subscribe(() => {
        const message = this.translocoService.translate('modify-schedule.editRecurringSuccess');
        this.messageService.add({
          severity: 'success',
          summary: this.translocoService.translate('modify-schedule.success'),
          detail: message,
        });
      });
    } catch (error) {
      console.error('Error opening edit recurring dialog:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('modify-schedule.error'),
        detail: this.translocoService.translate('modify-schedule.editRecurringError'),
      });
    }
  }

  private splitShift(): void {
    if (!this.selectedEvent) {
      return;
    }

    const eventId = this.selectedEvent.allDataEvent.getEventData()?.getId();
    if (!eventId) {
      return;
    }

    this.eventsService
      .getDoc(eventId)
      .pipe(
        take(1),
        filter(Boolean),
        switchMap((freshEventData) =>
          this.orgData.pipe(
            take(1),
            map((orgData) => ({ freshEventData, orgData })),
          ),
        ),
        switchMap(({ freshEventData, orgData }) => {
          const dialogRef = this.dynamicDialogService.open(EventSplitDialogComponent, {
            header: this.translocoService.translate('event-split-dialog.title'),
            width: '50%',
            style: { minWidth: '50%', maxWidth: '80vw' },
            contentStyle: { padding: '1.5rem' },
            data: {
              start: freshEventData.getStart(),
              end: freshEventData.getEnd(),
              orgData,
            },
          });

          return dialogRef.onClose.pipe(map((splitTime) => ({ splitTime, freshEventData })));
        }),
        filter(({ splitTime }) => !!splitTime),
        switchMap(({ splitTime, freshEventData }) => {
          const splitTransaction = getSplitTransaction(freshEventData, splitTime);

          return from(this.eventsService.runDBTransaction(splitTransaction)).pipe(
            map(() => splitTime),
            catchError((error) => {
              console.error('Error splitting shift:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translocoService.translate('modify-schedule.error'),
                detail: this.translocoService.translate('modify-schedule.splitShiftError'),
              });
              return of(null);
            }),
          );
        }),
        filter((splitTime) => !!splitTime),
        tap((splitTime) => {
          const message = this.translocoService.translate('modify-schedule.splitShiftSuccess', {
            date: splitTime.format('LL'),
          });
          this.messageService.add({
            severity: 'success',
            summary: this.translocoService.translate('modify-schedule.success'),
            detail: message,
          });
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private deleteShift(): void {
    if (!this.selectedEvent) {
      return;
    }

    const eventData = this.selectedEvent.allDataEvent.getEventData();
    const isRepeatingShift = eventData && eventData.getEventConfigId() !== EventDataSchema.Defaults.eventConfigId;

    if (isRepeatingShift) {
      this.confirmationService.confirm({
        header: this.translocoService.translate('modify-schedule.repeatingShiftDialogTitle'),
        message: this.translocoService.translate('modify-schedule.repeatingShiftDialogBody'),
        acceptLabel: this.translocoService.translate('modify-schedule.deleteThisShiftOnly'),
        rejectLabel: this.translocoService.translate('modify-schedule.deleteThisAndFollowingShifts'),
        acceptIcon: 'none',
        rejectIcon: 'none',
        rejectButtonStyleClass: 'p-button-text p-button-secondary',
        accept: () => this.onDeleteThisShiftOnly(),
        reject: (type?: ConfirmEventType) => {
          if (type === ConfirmEventType.REJECT) {
            this.onDeleteThisAndFollowingShifts().catch(() => {});
          } else {
            this.selectedEvent = null;
          }
        },
      });
      return;
    }

    this.confirmationService.confirm({
      header: this.translocoService.translate('modify-schedule.confirmDeleteTitle'),
      message: this.translocoService.translate('modify-schedule.confirmDeleteBody'),
      acceptLabel: this.translocoService.translate('modify-schedule.confirmDeleteAccept'),
      rejectLabel: this.translocoService.translate('modify-schedule.confirmDeleteReject'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.performDelete().catch((error) => {
          console.error('performDelete failed:', error);
        });
      },
    });
  }

  private async performDelete(): Promise<void> {
    if (!this.selectedEvent) {
      return;
    }

    try {
      const eventId = this.selectedEvent.id;
      const shiftDate = this.selectedEvent.startTime;

      await firstValueFrom(this.eventsService.delete(eventId));

      const message = this.translocoService.translate('modify-schedule.deleteShiftSuccess', { shiftDate });
      const summary = this.translocoService.translate('modify-schedule.deleteShiftSuccessSummary');

      this.messageService.add({
        severity: 'success',
        summary,
        detail: message,
      });

      this.selectedEvent = null;
    } catch (error) {
      console.error('Error deleting shift:', error);

      const message = this.translocoService.translate('modify-schedule.deleteShiftError');
      const summary = this.translocoService.translate('modify-schedule.deleteShiftErrorSummary');

      this.messageService.add({
        severity: 'error',
        summary,
        detail: message,
      });
    }
  }

  ////////////////////////////////////////////////////////////////////////
  // Repeating Shift Dialog Methods
  ////////////////////////////////////////////////////////////////////////

  private onDeleteThisShiftOnly(): void {
    if (!this.selectedEvent) {
      return;
    }

    this.performDelete().catch((error) => {
      console.error('performDelete failed:', error);
    });
  }

  private async onDeleteThisAndFollowingShifts(): Promise<void> {
    if (!this.selectedEvent) {
      return;
    }

    try {
      const eventData = this.selectedEvent.allDataEvent.getEventData();
      if (!eventData) {
        throw new Error('Event data not found');
      }

      const eventConfigId = eventData.getEventConfigId();
      if (!eventConfigId || eventConfigId === EventDataSchema.Defaults.eventConfigId) {
        throw new Error('Event config ID not found');
      }

      const allEvents = await firstValueFrom(
        this.eventsService.getDocsArray({
          query: [{ fieldPath: EventDataSchema.eventConfigId, opStr: '==', value: eventConfigId }],
        }),
      );

      const selectedShiftStart = this.selectedEvent.allDataEvent.getEventData()?.getStart();
      if (!selectedShiftStart) {
        throw new Error('Selected shift start time not found');
      }

      const followingShifts = allEvents.filter((event) => {
        const eventStart = event.getStart();
        return eventStart.isAfter(selectedShiftStart) && event.getId() !== this.selectedEvent?.id;
      });

      const unassignedFollowingShifts = followingShifts.filter((event) => {
        const assignedUserId = event.getAssignedUserId();
        const assignedBackupUserId = event.getAssignedBackupUserId();
        return !assignedUserId && !assignedBackupUserId;
      });

      const unassignedCount = unassignedFollowingShifts.length;
      const totalCount = 1 + unassignedCount;

      const title = translate('modify-schedule.confirmDeleteThisAndFollowingTitle');
      const body = translate('modify-schedule.confirmDeleteThisAndFollowingBody', {
        totalCount,
        unassignedCount,
      });

      this.dynamicDialogService
        .open(ConfirmWithInputComponent, {
          header: title,
          width: '30%',
          data: {
            title,
            body,
            requiredConfirmationString: String(totalCount),
          },
        })
        .onClose.pipe(
          filter(Boolean),
          switchMap(() => this.performDeleteThisAndFollowing(unassignedFollowingShifts)),
          untilDestroyed(this),
        )
        .subscribe();
    } catch (error) {
      console.error('Error preparing delete this and following shifts dialog:', error);
      const message = this.translocoService.translate('modify-schedule.deleteShiftError');
      const summary = this.translocoService.translate('modify-schedule.deleteShiftErrorSummary');

      this.messageService.add({
        severity: 'error',
        summary,
        detail: message,
      });
    }
  }

  private async performDeleteThisAndFollowing(unassignedFollowingShifts: EventData[]): Promise<void> {
    if (!this.selectedEvent) {
      return;
    }

    try {
      const eventData = this.selectedEvent.allDataEvent.getEventData();
      if (!eventData) {
        throw new Error('Event data not found');
      }

      const eventConfigId = eventData.getEventConfigId();
      if (!eventConfigId || eventConfigId === EventDataSchema.Defaults.eventConfigId) {
        throw new Error('Event config ID not found');
      }

      await firstValueFrom(this.eventsService.delete(this.selectedEvent.id));

      if (unassignedFollowingShifts.length > 0) {
        const eventIds = unassignedFollowingShifts.map((event) => event.getId());
        await this.eventsService.deleteDocs(eventIds);
        console.log(`Deleted ${eventIds.length} unassigned following shifts for eventConfigId: ${eventConfigId}`);
      }

      const eventConfig = await firstValueFrom(this.eventConfigService.getDoc(eventConfigId));
      if (!eventConfig) {
        throw new Error('Event config not found');
      }

      const orgData = await firstValueFrom(this.orgData);
      const timezone = orgData.getTimezone();

      const updatedEventConfig = new EventConfig({
        id: eventConfig.getId(),
        start: eventConfig.getStart(timezone),
        end: eventConfig.getEnd(timezone),
        rrule: eventConfig.getRRule(),
        generate: false,
        type: eventConfig.getType(),
        color: eventConfig.getColor(),
        assignedUserId: eventConfig.getAssignedUserId(),
        assignedBackupUserId: eventConfig.getAssignedBackupUserId(),
        displayName: eventConfig.getDisplayName(),
        description: eventConfig.getDescription(),
        extendScheduleNumDays: eventConfig.getExtendScheduleNumDays(),
      });

      await firstValueFrom(this.eventConfigService.upload({ obj: updatedEventConfig, mode: 'update' }));

      const totalDeleted = 1 + unassignedFollowingShifts.length;
      const message = this.translocoService.translate('modify-schedule.deleteThisAndFollowingSuccess', {
        count: totalDeleted,
      });
      const summary = this.translocoService.translate('modify-schedule.deleteShiftSuccessSummary');

      this.messageService.add({
        severity: 'success',
        summary,
        detail: message,
      });

      this.selectedEvent = null;
    } catch (error) {
      console.error('Error deleting this and following shifts:', error);

      const message = this.translocoService.translate('modify-schedule.deleteShiftError');
      const summary = this.translocoService.translate('modify-schedule.deleteShiftErrorSummary');

      this.messageService.add({
        severity: 'error',
        summary,
        detail: message,
      });
    }
  }
}
